import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  useDraggable,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Reorder } from 'framer-motion';


const Postedvideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVideos, setSelectedVideos] = useState([]);

 // Drag and drop state
  const [isDragMode, setIsDragMode] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const [editingVideo, setEditingVideo] = useState(null); // for modal open
  const [editedTitle, setEditedTitle] = useState('');
  const [editedUrl, setEditedUrl] = useState('');

  const navigate = useNavigate();

  const [dragEnabledId, setDragEnabledId] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(null);


  // Fetch videos from backend

    useEffect(() => {
    const fetchvideos = async () => {
      try {
        const res = await axios.get('/api/videolist');
        console.log('response', res);
        setVideos(res.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching members:", error);
        setLoading(false);
      }
    };

    fetchvideos();
  }, []);
 

    // Filter videos based on search term
  const filteredVideos = videos.filter(video =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEmbedUrl = (url) => {
  if (!url) return "";

  // Handles: https://youtu.be/abc123
  if (url.includes("youtu.be")) {
    return url.replace("youtu.be/", "www.youtube.com/embed/");
  }

  // Handles: https://www.youtube.com/watch?v=abc123
  if (url.includes("watch?v=")) {
    return url.replace("watch?v=", "embed/");
  }

  return url; // already an embed URL
};


  const handleDoubleClick = useCallback((e, index) => {
    e.preventDefault();
    setIsDragMode(true);
    // Set the highlighted card
  setHighlightedIndex(index);
    
    // Auto-disable after 15 seconds
    setTimeout(() => {
      setIsDragMode(false);
    }, 15000);

    setTimeout(() => {
    setHighlightedIndex(null);
  }, 7000);

  }, []);

  const handleDragStart = useCallback((e, index) => {
    if (!isDragMode) {
      e.preventDefault();
      return;
    }
    
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  }, [isDragMode]);

  const handleDragOver = useCallback((e, index) => {
    if (!isDragMode || draggedIndex === null) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  }, [isDragMode, draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(async (e, dropIndex) => {
    e.preventDefault();
    
    if (!isDragMode || draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    // Reorder the videos array
    const newVideos = [...filteredVideos];
   
    const draggedVideo = newVideos[draggedIndex];
    
    // Remove from old position
    newVideos.splice(draggedIndex, 1);
    
    // Insert at new position
    newVideos.splice(dropIndex, 0, draggedVideo);
    
    // Update state
    setVideos(newVideos);
     console.log('newvideos', newVideos);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDragMode(false);

    // Send new order to backend
    try {
      
       const resp = await axios.put('/api/videosreorder', newVideos);
      

      console.log('Video order updated successfully');
      console.log(resp.message);
    } catch (error) {
      console.error('Error updating video order:', error);
      // Optionally show error message to user
    }
  }, [isDragMode, draggedIndex, filteredVideos]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);


   const getCardStyle = (index) => {
    const baseStyle = { ...styles.videoCard };
    
    if (isDragMode) {
      baseStyle.cursor = 'grab';
    }
    
    if (draggedIndex === index) {
      baseStyle.opacity = 0.6;
      baseStyle.cursor = 'grabbing';
    }
    
    if (dragOverIndex === index && draggedIndex !== index) {
      baseStyle.borderColor = '#007bff';
      baseStyle.backgroundColor = '#f8f9ff';
    }

     // Add highlighting style
  if (highlightedIndex === index) {
     baseStyle.border = '4px solid #007bff';
    baseStyle.borderRadius = '8px';
    //baseStyle.outline = '2px solid #0056b3';
    baseStyle.outlineOffset = '2px';
    baseStyle.backgroundColor = '#ffffff';
    baseStyle.position = 'relative';
    baseStyle.zIndex = '10';
    baseStyle.transition = 'all 0.2s ease-in-out';
  }
    
    return baseStyle;
  };

  

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    })
  );

  // Extract video ID from YouTube URL
  const getVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Get YouTube thumbnail
  const getThumbnail = (url) => {
    const videoId = getVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
  };

  

  // Handle video selection
  const handleVideoSelect = (videoId) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedVideos.length === filteredVideos.length) {
      setSelectedVideos([]);
    } else {
      setSelectedVideos(filteredVideos.map(video => video._id));
    }
  };

  // Handle delete selected (placeholder function)
  const handleDeleteSelected = () => {
    if (selectedVideos.length > 0) {
      const confirmed = window.confirm(`Are you sure you want to delete ${selectedVideos.length} video(s)?`);
      if (confirmed) {
        // Here you would make API call to delete videos
        console.log('Deleting videos:', selectedVideos);
        // Remove from local state for demo
        setVideos(prev => prev.filter(video => !selectedVideos.includes(video._id)));
        setSelectedVideos([]);
      }
    }
  };

  const handleEditVideo = (id) => {
  const videoToEdit = videos.find((vid) => vid._id === id);
  setEditingVideo(videoToEdit);
  setEditedTitle(videoToEdit.title);
  setEditedUrl(videoToEdit.youtubeUrl);
};

const handleSaveEdit = async () => {
  if (!editingVideo) return;

  try {
    const response = await fetch(`/api/videos/${editingVideo._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: editedTitle,
        youtubeUrl: editedUrl,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      // Update frontend instantly
      setVideos((prevVideos) =>
        prevVideos.map((vid) =>
          vid._id === editingVideo._id ? { ...vid, title: editedTitle, youtubeUrl: editedUrl } : vid
        )
      );
      alert(data.message);
      setEditingVideo(null);
    } else {
      alert(data.message || 'Failed to update video');
    }
  } catch (error) {
    console.error('Error updating video:', error);
    alert('Error updating video');
  }
};

  // Handle individual video delete
  const handleDeleteVideo = async (videoId, title) => {
    const confirmed = window.confirm(`Are you sure you want to delete "${title}"?`);
    if (!confirmed) return;
    try {
    const res = await axios.delete(`/api/deletevideos/${videoId}`);
    console.log('Deleted video:', res.data);
    setVideos(prev => prev.filter(video => video._id !== videoId));
     } catch (error) {
    console.error('Error deleting video:', error.response?.data?.message || error.message);
    alert(`Failed to delete video: ${error.response?.data?.message || error.message}`);
  }

  };

  const SortableVideoCard = ({ video }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useDraggable({
  id: video._id,
  disabled: dragEnabledId !== video._id,
});


    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
     
      cursor: dragEnabledId === video._id ? 'grab' : 'default',
      boxShadow: dragEnabledId === video._id ? '0 0 0 2px #007bff' : 'none',

    };

    return (
      <div
        ref={setNodeRef}
        style={{ ...styles.videoCard, ...style }}
        {...attributes}
        {...(dragEnabledId === video._id ? listeners : {})}
        onDoubleClick={() => handleDoubleClick(video._id)}
      >
        <div style={styles.cardHeader}>
          <input
            type="checkbox"
            checked={selectedVideos.includes(video._id)}
            onChange={() => handleVideoSelect(video._id)}
            style={styles.checkbox}
           />
          <button
            style={styles.deleteIconButton}
            onClick={() => handleDeleteVideo(video._id, video.title)}
            title="Delete video"
          >
            🗑️
          </button>
        </div>

        <div style={styles.thumbnailContainer}>
          <img
            src={getThumbnail(video.youtubeUrl)}
            alt={video.title}
            style={styles.thumbnail}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={styles.thumbnailPlaceholder}>
            <span style={styles.placeholderIcon}>📹</span>
            <span style={styles.placeholderText}>No thumbnail</span>
          </div>
        </div>
         <div style={styles.cardContent}>
          <h3 style={styles.videoTitle}>{video.title}</h3>
          <div style={styles.urlContainer}>
            <a
              href={video.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.videoUrl}
            >
              {video.youtubeUrl.length > 40
                ? video.youtubeUrl.substring(0, 40) + '...'
                : video.youtubeUrl}
            </a>
          </div>
        </div>

        <div style={styles.cardActions}>
          <button
            style={styles.viewButton}
            onClick={() => window.open(video.youtubeUrl, '_blank')}
          >
            View
          </button>
        </div>
      </div>
    );
  };




  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading videos...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
    
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Video Management</h1>
          <p style={styles.subtitle}>Manage your YouTube video collection</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.addButton} onClick={() => navigate('/dashboard/videos/addvideo')}>
            + Add New Video
          </button>
        </div>
      </div>

     
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{videos.length}</div>
          <div style={styles.statLabel}>Total Videos</div>
        </div>
        {/* <div style={styles.statCard}>
          <div style={styles.statNumber}>{selectedVideos.length}</div>
          <div style={styles.statLabel}>Selected</div>
        </div> */}
       
      </div>

     
      <div style={styles.actionBar}>
        <div style={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search videos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        {/* <div style={styles.bulkActions}>
          <button 
            style={styles.selectAllButton}
            onClick={handleSelectAll}
          >
            {selectedVideos.length === filteredVideos.length ? 'Deselect All' : 'Select All'}
          </button>
          {selectedVideos.length > 0 && (
            <button 
              style={styles.deleteButton}
              onClick={handleDeleteSelected}
            >
              Delete Selected ({selectedVideos.length})
            </button>
          )}
        </div> */}
      </div>

      {/* Videos List */}
      {error && (
        <div style={styles.errorMessage}>
          <span style={styles.errorIcon}>⚠</span>
          Using mock data - {error}
        </div>
      )}

      {filteredVideos.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📹</div>
          <h3 style={styles.emptyTitle}>No videos found</h3>
          <p style={styles.emptyText}>
            {searchTerm ? 'No videos match your search criteria.' : 'Start by adding your first video.'}
          </p>
        </div>
      ) : (
  

        <div style={styles.videoGrid}>
          {filteredVideos.map((video, index) => (
            <div key={video._id} 
            //style={styles.videoCard}
            style={getCardStyle(index)}
              draggable={isDragMode}
              onDoubleClick={(e) => handleDoubleClick(e, index)}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div style={styles.cardHeader}>
                {/* <input
                  type="checkbox"
                  checked={selectedVideos.includes(video._id)}
                  onChange={() => handleVideoSelect(video._id)}
                  style={styles.checkbox}
                /> */}
                <button
                  style={styles.deleteIconButton}
                  onClick={() => handleDeleteVideo(video._id, video.title)}
                  title="Delete video"
                >   
                  X
                </button>
              </div>
              
              <div style={styles.thumbnailContainer}>
                <img
                  src={getThumbnail(video.youtubeUrl)}
                  alt={video.title}
                  style={styles.thumbnail}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div style={styles.thumbnailPlaceholder}>
                  <span style={styles.placeholderIcon}>📹</span>
                  <span style={styles.placeholderText}>No thumbnail</span>
                </div>
              </div>

              <div style={styles.cardContent}>
                <h3 style={styles.videoTitle}>{video.title}</h3>
                <div style={styles.urlContainer}>
                  <a
                    href={video.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.videoUrl}
                  >
                    {video.youtubeUrl.length > 40 
                      ? video.youtubeUrl.substring(0, 40) + '...' 
                      : video.youtubeUrl
                    }
                  </a>
                </div>
              </div>

              <div style={styles.cardActions}>
               
                <button 
                  style={styles.viewButton}
                  onClick={() => window.open(video.youtubeUrl, '_blank')}
                >
                  View
                </button>
                <button 
                  style={styles.viewButton}
                  onClick={() => handleEditVideo(video._id)}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}


        {editingVideo && (
  <div style={styles.modalOverlay}>
    <div style={styles.modalContent}>
      <h2>Edit Video Details</h2>
      {/* <iframe
        src={editingVideo.youtubeUrl}
        alt={editingVideo.title}
        style={{ width: '100%', borderRadius: '10px', marginBottom: '1rem' }}
        controls
      /> */}
       <iframe
        src={getEmbedUrl(editingVideo.youtubeUrl)}
        title={editingVideo.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ width: '100%', borderRadius: '10px', marginBottom: '1rem' }}
        />
      <div>
        <label style={styles.label}>Title</label>
        <input
          type="text"
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          style={styles.input}
        />
      </div>
      <div>
        <label style={styles.label}>Youtube URL</label>
        <textarea
          value={editedUrl}
          onChange={(e) => setEditedUrl(e.target.value)}
          style={styles.textarea}
        />
      </div>
      <div style={styles.modalActions}>
        <button style={styles.saveBtn} onClick={handleSaveEdit}>Save</button>
        <button style={styles.cancelBtn} onClick={() => setEditingVideo(null)}>Cancel</button>
      </div>
    </div>
  </div>
        )}
    </div>
  );
};

// Styles object
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc'
  },
  spinner: {
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: '20px',
    color: '#64748b',
    fontSize: '16px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px'
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#1e293b',
    margin: '0 0 8px 0'
  },
  subtitle: {
    color: '#64748b',
    fontSize: '1.1rem',
    margin: 0
  },
  headerActions: {
    display: 'flex',
    gap: '12px'
  },
  addButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    textAlign: 'center'
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#3b82f6',
    marginBottom: '8px'
  },
  statLabel: {
    color: '#64748b',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  searchContainer: {
    flex: '1',
    minWidth: '300px'
  },
  searchInput: {
    width: '100%',
    maxWidth: '400px',
    padding: '12px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s ease'
  },
  bulkActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  selectAllButton: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '2px solid #e2e8f0',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  deleteButton: {
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  errorMessage: {
    backgroundColor: '#fef3cd',
    color: '#8a2be2',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  errorIcon: {
    fontSize: '20px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  emptyIcon: {
    fontSize: '4rem',
    marginBottom: '20px'
  },
  emptyTitle: {
    fontSize: '1.5rem',
    color: '#1e293b',
    marginBottom: '12px'
  },
  emptyText: {
    color: '#64748b',
    fontSize: '1rem'
  },
  videoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '24px'
  },
  videoCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    position: 'relative'
  },
  cardHeader: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    right: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 2
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  deleteIconButton: {
    backgroundColor: 'rgba(239, 68, 68, 0.9)',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 8px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease'
  },
  thumbnailContainer: {
    position: 'relative',
    height: '200px',
    backgroundColor: '#f1f5f9'
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  thumbnailPlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'none',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f1f5f9',
    color: '#64748b'
  },
  placeholderIcon: {
    fontSize: '2rem',
    marginBottom: '8px'
  },
  placeholderText: {
    fontSize: '0.9rem'
  },
  cardContent: {
    padding: '20px'
  },
  videoTitle: {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '12px',
    lineHeight: '1.4'
  },
  urlContainer: {
    marginBottom: '16px'
  },
  videoUrl: {
    color: '#3b82f6',
    textDecoration: 'none',
    fontSize: '0.9rem',
    wordBreak: 'break-all'
  },
  cardActions: {
    padding: '16px 20px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    gap: '12px'
  },
  editButton: {
    backgroundColor: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1
  },
  viewButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1
  },

  modalOverlay: {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  margin: '0 auto',
  // backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
},
modalContent: {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '2rem',
  width: '100%',
  maxHeight: '80vh', // prevents it from going full height
  overflowY: 'auto', // scroll only inside modal if needed
  maxWidth: '500px',
  boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
},
label: {
  display: 'block',
  marginBottom: '0.5rem',
  fontWeight: '600',
  color: '#374151',
},
input: {
  width: '100%',
  padding: '0.5rem',
  marginBottom: '1rem',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
},
textarea: {
  width: '100%',
  padding: '0.5rem',
  minHeight: '80px',
  marginBottom: '1rem',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
},
modalActions: {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
},
saveBtn: {
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  padding: '0.5rem 1rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
},
cancelBtn: {
  backgroundColor: '#e5e7eb',
  color: '#374151',
  border: 'none',
  padding: '0.5rem 1rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
},
};

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

input:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.video-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}
`;
document.head.appendChild(styleSheet);

export default Postedvideos;